import React, { useState } from 'react';
import { Users, BarChart2, Flag, Plus, PlusCircle } from 'lucide-react';
import CharacterCard, { type Character } from './CharacterCard';
import type { Poll } from '../hooks/usePolls';
import type { Milestone } from '../hooks/useMilestones';
import PollCard from './PollCard';
import MilestoneCard from './MilestoneCard';

type IdeaSidebarProps = {
  characters: Character[];
  recentPolls: Poll[];
  milestones: Milestone[];
  isAuthor: boolean;
  onCreatePoll: () => void;
  onCreateMilestone: () => void;
  onCreateCharacter: () => void;
  onToggleCharacterFavorite: (characterId: string) => Promise<void>;
  onVotePoll: (pollId: string, optionId: string) => Promise<void>;
  onClosePoll?: (pollId: string) => Promise<void>;
  onDeletePoll?: (pollId: string) => Promise<void>;
  onUpdateMilestone?: (milestoneId: string, updates: Partial<Milestone>) => Promise<void>;
  onDeleteMilestone?: (milestoneId: string) => Promise<void>;
};

export default function IdeaSidebar({
  characters,
  recentPolls,
  milestones,
  isAuthor,
  onCreatePoll,
  onCreateMilestone,
  onCreateCharacter,
  onToggleCharacterFavorite,
  onVotePoll,
  onClosePoll,
  onDeletePoll,
  onUpdateMilestone,
  onDeleteMilestone
}: IdeaSidebarProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleAction = (action: 'poll' | 'milestone' | 'character') => {
    setIsMenuOpen(false);
    switch (action) {
      case 'poll':
        onCreatePoll();
        break;
      case 'milestone':
        onCreateMilestone();
        break;
      case 'character':
        onCreateCharacter();
        break;
    }
  };

  const EmptyState = ({ 
    title, 
    description, 
    action, 
    onClick 
  }: { 
    title: string; 
    description: string; 
    action?: string;
    onClick?: () => void;
  }) => (
    <div className="text-center py-8 px-4">
      <PlusCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
      <h4 className="text-lg font-medium text-gray-900 mb-2">{title}</h4>
      <p className="text-gray-500 mb-4">{description}</p>
      {action && onClick && (
        <button
          onClick={onClick}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm text-indigo-600 hover:text-indigo-800"
        >
          <Plus className="w-4 h-4" />
          {action}
        </button>
      )}
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Add Content Button */}
      {isAuthor && (
        <div className="relative flex justify-end">
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Content
          </button>

          {isMenuOpen && (
            <>
              <div 
                className="fixed inset-0 z-10"
                onClick={() => setIsMenuOpen(false)}
              />
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border z-20">
                <button
                  onClick={() => handleAction('poll')}
                  className="w-full px-4 py-2 text-left flex items-center gap-2 hover:bg-gray-50"
                >
                  <BarChart2 className="w-4 h-4" />
                  Create Poll
                </button>
                <button
                  onClick={() => handleAction('milestone')}
                  className="w-full px-4 py-2 text-left flex items-center gap-2 hover:bg-gray-50"
                >
                  <Flag className="w-4 h-4" />
                  Add Milestone
                </button>
                <button
                  onClick={() => handleAction('character')}
                  className="w-full px-4 py-2 text-left flex items-center gap-2 hover:bg-gray-50"
                >
                  <Users className="w-4 h-4" />
                  Add Character
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Characters Section */}
      <section className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <Users 
            className="w-5 h-5" 
            style={{ color: 'var(--color-accent1)' }}
          />
          <h2 
            className="text-lg font-semibold"
            style={{ color: 'var(--color-font)' }}
          >
            Characters
          </h2>
        </div>
        <div className="space-y-4">
          {characters.length > 0 ? (
            characters.map(character => (
              <CharacterCard
                key={character.id}
                character={character}
                onToggleFavorite={onToggleCharacterFavorite}
              />
            ))
          ) : (
            <EmptyState
              title="No Characters Yet"
              description={isAuthor 
                ? "Start bringing your story to life by adding characters. Define their roles, personalities, and watch them evolve."
                : "No characters have been added to this story yet. Check back later for updates!"}
              action={isAuthor ? "Add First Character" : undefined}
              onClick={isAuthor ? onCreateCharacter : undefined}
            />
          )}
        </div>
      </section>

      {/* Recent Polls Section */}
      <section className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <BarChart2 
            className="w-5 h-5" 
            style={{ color: 'var(--color-accent1)' }}
          />
          <h2 
            className="text-lg font-semibold"
            style={{ color: 'var(--color-font)' }}
          >
            Recent Polls
          </h2>
        </div>
        <div className="space-y-4">
          {recentPolls.length > 0 ? (
            recentPolls.map(poll => (
              <PollCard
                key={poll.id}
                poll={poll}
                onVote={onVotePoll}
                onClose={onClosePoll}
                onDelete={onDeletePoll}
                isAuthor={isAuthor}
              />
            ))
          ) : (
            <EmptyState
              title="No Active Polls"
              description={isAuthor 
                ? "Create polls to gather feedback from your readers on story elements, character decisions, or cover designs."
                : "No polls have been created for this story yet. Check back later to participate in future polls!"}
              action={isAuthor ? "Create First Poll" : undefined}
              onClick={isAuthor ? onCreatePoll : undefined}
            />
          )}
        </div>
      </section>

      {/* Milestones Section */}
      <section className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <Flag 
            className="w-5 h-5" 
            style={{ color: 'var(--color-accent1)' }}
          />
          <h2 
            className="text-lg font-semibold"
            style={{ color: 'var(--color-font)' }}
          >
            Milestones
          </h2>
        </div>
        <div className="space-y-4">
          {milestones.length > 0 ? (
            milestones.map(milestone => (
              <MilestoneCard
                key={milestone.id}
                milestone={milestone}
                onUpdate={onUpdateMilestone}
                onDelete={onDeleteMilestone}
                isAuthor={isAuthor}
              />
            ))
          ) : (
            <EmptyState
              title="No Milestones Set"
              description={isAuthor 
                ? "Track your story's progress by adding milestones. Set goals, mark your achievements, and keep readers informed."
                : "No milestones have been set for this story yet. Check back to follow the author's progress!"}
              action={isAuthor ? "Add First Milestone" : undefined}
              onClick={isAuthor ? onCreateMilestone : undefined}
            />
          )}
        </div>
      </section>
    </div>
  );
}